import { useState } from 'react';
import { Hairdresser } from '../types/entities';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const useHairdressersApi = () => {
  const [error, setError] = useState<Error | null>(null);
  const { handleApiError } = useErrorHandler();

  const handleError = (err: any) => {
    console.error('API Error:', err);
    const apiError = handleApiError(err);
    setError(new Error(apiError.message));
    throw new Error(apiError.message);
  };

  const fetchHairdressers = async (): Promise<Hairdresser[]> => {
    try {
      setError(null);
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/hairdressers`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch hairdressers: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (err) {
      handleError(err);
      return [];
    }
  };

  const createHairdresser = async (hairdresserData: { name: string }): Promise<Hairdresser> => {
    try {
      setError(null);
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/hairdressers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(hairdresserData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create hairdresser: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  const updateHairdresser = async (id: number, hairdresserData: { name: string }): Promise<Hairdresser> => {
    try {
      setError(null);
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/hairdressers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(hairdresserData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update hairdresser: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  const deleteHairdresser = async (id: number): Promise<void> => {
    try {
      setError(null);
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/hairdressers/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete hairdresser: ${response.statusText}`);
      }
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  return {
    error,
    fetchHairdressers,
    createHairdresser,
    updateHairdresser,
    deleteHairdresser,
  };
};
